import { Link } from "react-router-dom";
import { useI18n, BRAND } from "@/i18n";
import { ArrowLeft } from "lucide-react";

export default function CookiePolicy() {
  const { t } = useI18n();
  const p = t.cookiePolicy;

  const openSettings = () => {
    window.dispatchEvent(new CustomEvent("ecv:openCookieSettings"));
  };

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-[#2C2520]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#2C2520]/60 hover:text-[#C85A3C] transition"
          data-testid="cookie-policy-back"
        >
          <ArrowLeft className="w-4 h-4" /> {p.back}
        </Link>

        <p className="overline text-[#C85A3C] mt-8">{p.overline}</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-2" data-testid="cookie-policy-title">
          {p.title}
        </h1>
        <p className="mt-3 text-sm text-[#2C2520]/60">{p.lastUpdated}: {p.updateDate}</p>

        <div className="mt-10 space-y-10">
          <section>
            <p className="text-base leading-relaxed">{p.intro}</p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">{p.whatAreTitle}</h2>
            <p className="text-base leading-relaxed">{p.whatAreBody}</p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">{p.typesTitle}</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-lg">{p.necessaryTitle}</h3>
                <p className="mt-1 text-base leading-relaxed">{p.necessaryBody}</p>
              </div>
              <div>
                <h3 className="font-medium text-lg">{p.analyticsTitle}</h3>
                <p className="mt-1 text-base leading-relaxed">{p.analyticsBody}</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">{p.tableTitle}</h2>
            <div className="overflow-x-auto rounded-sm border border-[#2C2520]/10 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[#2C2520]/5">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">{p.thName}</th>
                    <th className="text-left px-4 py-3 font-medium">{p.thProvider}</th>
                    <th className="text-left px-4 py-3 font-medium">{p.thPurpose}</th>
                    <th className="text-left px-4 py-3 font-medium">{p.thDuration}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2C2520]/10">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">_ga, _ga_*</td>
                    <td className="px-4 py-3">Google Analytics</td>
                    <td className="px-4 py-3">{p.rowGaPurpose}</td>
                    <td className="px-4 py-3">{p.duration2y}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">ph_*</td>
                    <td className="px-4 py-3">PostHog</td>
                    <td className="px-4 py-3">{p.rowPhPurpose}</td>
                    <td className="px-4 py-3">{p.duration1y}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">ecv_cookie_consent</td>
                    <td className="px-4 py-3">Eternal City View</td>
                    <td className="px-4 py-3">{p.rowConsentPurpose}</td>
                    <td className="px-4 py-3">{p.duration6m}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">ecv_lang</td>
                    <td className="px-4 py-3">Eternal City View</td>
                    <td className="px-4 py-3">{p.rowLangPurpose}</td>
                    <td className="px-4 py-3">{p.durationPersistent}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">__stripe_*</td>
                    <td className="px-4 py-3">Stripe</td>
                    <td className="px-4 py-3">{p.rowStripePurpose}</td>
                    <td className="px-4 py-3">{p.duration1y}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">{p.manageTitle}</h2>
            <p className="text-base leading-relaxed">{p.manageBody}</p>
            <button
              onClick={openSettings}
              data-testid="cookie-policy-open-settings"
              className="mt-5 inline-block px-5 py-2.5 bg-[#C85A3C] text-white text-sm font-medium tracking-wide hover:bg-[#B24A2E] transition rounded-sm"
            >
              {p.manageCta}
            </button>
            <p className="mt-4 text-sm text-[#2C2520]/70 leading-relaxed">{p.browserNote}</p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">{p.rightsTitle}</h2>
            <p className="text-base leading-relaxed">{p.rightsBody}</p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">{p.contactTitle}</h2>
            <p className="text-base leading-relaxed">
              {p.contactBody}{" "}
              <a href={`mailto:${BRAND.email}`} className="underline text-[#C85A3C]" data-testid="cookie-policy-contact-email">
                {BRAND.email}
              </a>
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-[#2C2520]/10">
          <Link
            to="/"
            className="text-sm text-[#2C2520]/60 hover:text-[#C85A3C] transition"
            data-testid="cookie-policy-home"
          >
            ← {p.back}
          </Link>
        </div>
      </div>
    </div>
  );
}
